import React, { useMemo, useState } from 'react';

import { theme } from '~/theme';

import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  styled,
} from '@mui/material';

import { AvailabilityDebugResponse, RemovedRate } from '~/services/AccommodationService';

import MergedRatesContent from './MergedRatesContent';

const SidebarDrawer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: '100%',
  height: '100%',
  width: 300,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  borderLeft: `1px solid ${theme.palette.divider}`,
  transition: 'width 0.3s ease',
  overflow: 'visible',
  boxShadow: theme.shadows[1],
}));

const SidebarContent = styled(Box)({
  width: 300,
  height: '100%',
  overflow: 'auto',
});

interface RoomWithMissingRates {
  id: string;
  name: string;
  missingRates: Array<RemovedRate>;
}

// Reasons a Flash rate is dropped before pricing; the rest read fine as their raw code.
const REMOVED_RATE_REASON_LABELS: Partial<Record<RemovedRate['reason'], string>> = {
  'missing-package-price': 'Flash package has no price in this currency',
  'package-not-valid': 'Package not valid for this stay',
};

const getRateName = (rate: RemovedRate): string => {
  if (rate.reason === 'merged-rate') {
    return (rate.rate?.name as string) ?? rate.id;
  }
  return rate.id;
};

interface MissingRatesModalProps {
  availabilityDebugResponse: AvailabilityDebugResponse;
  closeModal: () => void;
}

export default function MissingRatesModal({ availabilityDebugResponse, closeModal }: MissingRatesModalProps) {
  const roomsWithMissingRates = availabilityDebugResponse.debug.missingRates.reduce<
    Record<string, RoomWithMissingRates>
  >((acc, rate) => {
    if (!rate.roomId) {
      return acc;
    }
    if (!acc[rate.roomId]) {
      const room = Object.values(availabilityDebugResponse.response.properties)[0].rooms[rate.roomId];
      acc[rate.roomId] = { id: rate.roomId, name: room.name, missingRates: [] };
    }
    acc[rate.roomId].missingRates.push(rate);
    return acc;
  }, {});

  const [selectedRate, setSelectedRate] = useState<RemovedRate>(
    Object.values(roomsWithMissingRates)[0].missingRates[0],
  );

  const selectedRoom = useMemo(() => {
    return roomsWithMissingRates[selectedRate.roomId];
  }, [selectedRate, roomsWithMissingRates]);

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'visible',
        },
      }}
    >
      <SidebarDrawer>
        <SidebarContent>
          <Box width={300} p={2}>
            <Typography variant="h6">Rooms</Typography>
            {Object.values(roomsWithMissingRates).map((room) => (
              <Accordion key={room.id} defaultExpanded={selectedRate.roomId === room.id}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="bold" fontSize="small">
                    {room.name}
                  </Typography>
                </AccordionSummary>
                <List>
                  {room.missingRates.map((rate) => (
                    <ListItem disablePadding key={rate.id}>
                      <ListItemButton onClick={() => setSelectedRate(rate)} selected={selectedRate.id === rate.id}>
                        <ListItemText primary={<Typography fontSize="small">{getRateName(rate)}</Typography>} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Accordion>
            ))}
          </Box>
        </SidebarContent>
      </SidebarDrawer>
      <DialogTitle>
        <Stack>
          <Typography variant="body1">
            {getRateName(selectedRate)}: ({REMOVED_RATE_REASON_LABELS[selectedRate.reason] ?? selectedRate.reason})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedRoom?.name}
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {selectedRate.reason === 'merged-rate' && (
          <MergedRatesContent availabilityDebugResponse={availabilityDebugResponse} mergedRate={selectedRate} />
        )}
        {selectedRate.reason !== 'merged-rate' && <Typography>{selectedRate.description}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
